import { useState, useCallback } from "react";
import { CreateCommentService } from "shared/services/comment";
import { CreateReplyService } from "shared/services/reply";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import { Message } from "shared/types/chatBoard";
import { stripHtmlTags, mapCommentToMessage, mergeAndSortMessages, sortRepliesChronologically } from "shared/utils/chatBoardUtils";
import * as io from "socket.io-client";

interface UseChatBoardFormsProps {
  chatBoardId?: string | number;
  auth: any;
  socketRef: React.RefObject<io.Socket | null>;
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  selectedMessage: Message | null;
  setSelectedMessage: React.Dispatch<React.SetStateAction<Message | null>>;
  setReplies: React.Dispatch<React.SetStateAction<{ [commentId: string]: any[] }>>;
}

export default function useChatBoardForms({
  chatBoardId,
  auth,
  socketRef,
  messagesContainerRef,
  setMessages,
  selectedMessage,
  setSelectedMessage,
  setReplies,
}: UseChatBoardFormsProps) {
  const [message, setMessage] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  const handleFileSelect = useCallback((files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files]);
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleReplyFileSelect = useCallback((files: File[]) => {
    setReplyFiles((prev) => [...prev, ...files]);
  }, []);

  const handleRemoveReplyFile = useCallback((index: number) => {
    setReplyFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmitComment = useCallback(async () => {
    if (!chatBoardId) {
      toastMessage("error", "Chat board ID is missing");
      return;
    }

    const htmlContent = message.trim();
    const plainTextContent = stripHtmlTags(htmlContent);

    if (!plainTextContent.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();

      formData.append("content", htmlContent);

      selectedFiles.forEach((file) => {
        const fileType = file.type;
        if (fileType.startsWith("image/")) {
          formData.append("images", file);
        } else if (fileType === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
          formData.append("pdfs", file);
        }
      });

      if (socketRef.current && socketRef.current.connected && chatBoardId) {
        const roomData = {
          chat_board_id: chatBoardId.toString(),
        };
        socketRef.current.emit("chat_board", roomData);
      } else {
      }

      const response = await CreateCommentService(chatBoardId, formData);

      if (response.status === 200 || response.status === 201) {
        const responseData = response.data?.data || response.data || {};

        let newCommentData: any = {};

        if (responseData.comment_id || responseData.id) {
          newCommentData = responseData;
        } else {
          newCommentData = {
            comment_id: `temp-${Date.now()}`,
            content: htmlContent,
            created_at: new Date().toISOString(),
            user: {
              display_name: auth?.user?.display_name || auth?.user?.name ||
                (auth?.user?.first_name && auth?.user?.last_name
                  ? `${auth.user.first_name} ${auth.user.last_name}`
                  : auth?.user?.first_name || auth?.user?.last_name || "You"),
              role: auth?.user?.user_type || auth?.user?.role || "User",
            },
            comment_assets: selectedFiles.map((file) => {
              const fileType = file.type;
              let type = "document";
              if (fileType.startsWith("image/")) {
                type = "image";
              } else if (fileType === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
                type = "pdf";
              }

              return {
                type: type,
                name: file.name,
                path: URL.createObjectURL(file),
                mime: file.type,
              };
            }),
          };
        }

        const newMessage = mapCommentToMessage(newCommentData);

        setMessages((prev) => {
          const newMsgId = newMessage.id.toString();

          const existsById = prev.some((msg) => {
            const msgId = msg.id.toString();
            return msgId === newMsgId;
          });

          if (existsById) {
            return prev;
          }

          if (!newMsgId.startsWith("temp-")) {
            const existsByContent = prev.some((msg) => {
              const msgId = msg.id.toString();
              if (msgId.startsWith("temp-")) {
                return false;
              }
              return (
                msg.content === newMessage.content &&
                msg.sender.name === newMessage.sender.name &&
                msgId !== newMsgId
              );
            });

            if (existsByContent) {
              return prev;
            }
          }

          return mergeAndSortMessages([...prev, newMessage]);
        });

        setMessage("");
        setSelectedFiles([]);

        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [chatBoardId, message, selectedFiles, socketRef, auth, setMessages, messagesContainerRef]);

  const handleSubmitReply = useCallback(async () => {
    if (!selectedMessage) {
      toastMessage("error", "No message selected for reply");
      return;
    }

    const htmlContent = replyMessage.trim();
    const plainTextContent = stripHtmlTags(htmlContent);

    if (!plainTextContent.trim()) {
      return;
    }

    try {
      setIsSubmittingReply(true);
      const formData = new FormData();

      formData.append("content", htmlContent);

      replyFiles.forEach((file) => {
        const fileType = file.type;
        if (fileType.startsWith("image/")) {
          formData.append("images", file);
        } else if (fileType === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
          formData.append("pdfs", file);
        }
      });

      const response = await CreateReplyService(selectedMessage.id, formData);

      if (response.status === 200 || response.status === 201) {
        setReplyMessage("");
        setReplyFiles([]);

        const responseData = response.data?.data || response.data || {};
        const replyCreatedAt = responseData.created_at || responseData.createdAt || new Date().toISOString();
        const replyId = responseData.reply_id || responseData.id;

        if (replyId) {
          const optimisticReply = {
            reply_id: replyId,
            id: replyId,
            comment_id: selectedMessage.id,
            content: htmlContent,
            created_at: replyCreatedAt,
            createdAt: replyCreatedAt,
            user: {
              display_name: auth?.user?.display_name || auth?.user?.name ||
                (auth?.user?.first_name && auth?.user?.last_name
                  ? `${auth.user.first_name} ${auth.user.last_name}`
                  : auth?.user?.first_name || auth?.user?.last_name || "You"),
              user_type: auth?.user?.user_type || auth?.user?.role || "User",
              role: auth?.user?.user_type || auth?.user?.role || "User",
              profile_asset: auth?.user?.profile_asset || "",
            },
            reply_assets: replyFiles.map((file) => {
              const fileType = file.type;
              let type = "document";
              if (fileType.startsWith("image/")) {
                type = "image";
              } else if (fileType === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
                type = "pdf";
              }

              return {
                type: type,
                name: file.name,
                path: URL.createObjectURL(file),
                mime: file.type,
              };
            }),
          };

          setReplies((prev) => {
            const existingReplies = prev[selectedMessage.id] || [];
            const replyExists = existingReplies.some(
              (r: any) => (r.reply_id || r.id)?.toString() === replyId.toString()
            );
            
            if (replyExists) {
              return prev;
            }

            return {
              ...prev,
              [selectedMessage.id]: sortRepliesChronologically([...existingReplies, optimisticReply]),
            };
          });
        }

          setMessages((prev) => {
            const messageIndex = prev.findIndex((msg) => msg.id === selectedMessage.id);
            if (messageIndex !== -1) {
              const updatedMessages = [...prev];
              const currentMessage = updatedMessages[messageIndex];
              const currentRepliesCount = currentMessage.repliesCount !== undefined ? currentMessage.repliesCount : 0;
              const updatedMessage = {
                ...currentMessage,
                repliesCount: currentRepliesCount + 1,
                lastReplyAt: replyCreatedAt,
              };
              updatedMessages[messageIndex] = updatedMessage;
              
              setSelectedMessage((prevSelected) => {
                if (prevSelected && prevSelected.id === selectedMessage.id) {
                  return updatedMessage;
                }
                return prevSelected;
              });
              
              return updatedMessages;
            }
            return prev;
          });
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setIsSubmittingReply(false);
    }
  }, [selectedMessage, replyMessage, replyFiles, socketRef, setMessages, setReplies, auth]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseReplyPanel = useCallback(() => {
    setSelectedMessage(null);
    setReplyMessage("");
    setReplyFiles([]);
  }, [setSelectedMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    message,
    setMessage,
    replyMessage,
    setReplyMessage,
    selectedFiles,
    replyFiles,
    isSubmitting,
    isSubmittingReply,
    handleFileSelect,
    handleRemoveFile,
    handleReplyFileSelect,
    handleRemoveReplyFile,
    handleSubmitComment,
    handleSubmitReply,
    handleCloseReplyPanel,
  };
}

